import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import * as styles from '../DefaultTemplate.module.scss';
import { QualityAssessmentTemplateState } from '../../QualityAssessmentTemplate';
import {
  validate,
  LabelError,
  LabelErrorMessages,
  NameErrorMessages,
  NameError,
  NO_EQUIVALENT_VALUE,
  normalize,
} from '../../../components/editableFieldValidation';
import {
  AccordionContent,
  AccordionGroup,
  AccordionHeader,
  Button,
  FormContainer,
  FormControl,
  FormLabel,
  Icon,
  IconList,
  Input,
  Checkbox,
  TextArea,
  Text,
  Link,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'components/core';
import { useAccordionController } from 'components/core';

const MAX_DOMAIN_NAME_LENGTH = 1000;
const MAX_JUDGEMENT_LABEL_LENGTH = 255;

type Props = {
  name: string;
  onDelete: () => void;
  onDuplicate: () => void;
  disableDuplicate?: boolean;
};

const cochraneToRevManWeb = [
  // Any update to this list must also be reflected in Study::AssessmentDomain::COCHRANE_TO_REVMAN_WEB
  // See app/models/study/assessment_domain.rb
  // Keep these lowercase with no leading or trailing whitespace
  ['sequence generation', 'random sequence generation (selection bias)'],
  ['allocation concealment', 'allocation concealment (selection bias)'],
  [
    'blinding of participants and personnel',
    'blinding of participants and personnel (performance bias)',
  ],
  [
    'blinding of outcome assessors',
    'blinding of outcome assessment (detection bias)',
  ],
  ['incomplete outcome data', 'incomplete outcome data (attrition bias)'],
  ['selective outcome reporting', 'selective reporting (reporting bias)'],
  ['other sources of bias', 'other bias'],
];

export function cochraneEquivalent(newDomainName: string): string | null {
  const domainName = normalize(newDomainName);
  const pair = cochraneToRevManWeb.find((equivalentPair) => {
    return equivalentPair.includes(domainName);
  });
  if (pair)
    // find the name that's not equal to the original name
    return pair.filter((equivalentName) => equivalentName != domainName)[0];
  return null;
}

function showMessage(show: boolean) {
  if (show) {
    return (
      <Text variant="weak">
        Option cannot be changed as data has already been extracted
      </Text>
    );
  }
  return null;
}

const DomainForm = ({
  name,
  onDelete,
  onDuplicate,
  disableDuplicate = false,
}: Props) => {
  const {
    watch,
    register,
    getValues,
    setValue,
    trigger,
    getFieldState,
  } = useFormContext();
  const hasExtractedData: boolean = getValues(`${name}.prompt_before_deleting`);
  const domainName = getValues(`${name}.name`);
  const domainNameIsBlank = domainName == undefined || domainName.trim() == '';
  const judgements = useFieldArray<
    QualityAssessmentTemplateState,
    `domains.${number}.judgements`
  >({
    name: `${name}.judgements` as `domains.${number}.judgements`,
  });
  const judgementFieldIds = judgements.fields
    .map((field) => field.id)
    .join(',');

  const accordionEventHandler = useAccordionController();
  const lockAccordionIfFormInvalid = async () => {
    const domain = getValues(name);
    if (!domain) {
      // New domain has been deleted
      accordionEventHandler.unlock();
      return;
    }
    const valid = await trigger('domains');
    valid ? accordionEventHandler.unlock() : accordionEventHandler.lock();
  };

  useEffect(() => {
    for (let index = 0; index < judgements.fields.length; index += 1) {
      setValue(`${name}.judgements.${index}.position`, index + 1, {
        shouldDirty: false,
      });
    }
  }, [judgementFieldIds, judgements.fields.length, name, setValue]);

  return (
    <div className={styles.DomainForm}>
      <AccordionGroup
        className={styles.AccordionControl}
        initiallyOpen={domainNameIsBlank}
      >
        <AccordionHeader className={styles.DomainAccordionHeader}>
          <span className={styles.DomainAccordionHeaderText}>
            {watch(`${name}.name`)}
          </span>
        </AccordionHeader>
        <AccordionContent>
          <FormContainer hasPadding>
            <FormControl error={getFieldState(`${name}.name`).error?.message}>
              <FormLabel>Name</FormLabel>
              <Input
                {...register(`${name}.name`, {
                  required: { value: true, message: NameError.REQUIRED },
                  onBlur: lockAccordionIfFormInvalid,
                  validate: (value, formState) => {
                    const domains: QualityAssessmentTemplateState['domains'] =
                      formState.domains;
                    const domainNames = domains.map((o) => {
                      return { name: o.name };
                    });
                    return validate(
                      value,
                      domainNames,
                      [],
                      cochraneEquivalent(value),
                      MAX_DOMAIN_NAME_LENGTH,
                      NameErrorMessages
                    );
                  },
                })}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={domainNameIsBlank}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <TextArea {...register(`${name}.description`)} />
            </FormControl>
            <FormControl>
              <FormLabel>Judgements</FormLabel>
              <div className={styles.JudgementsList}>
                {judgements.fields.map((field, index) => {
                  const labelPath = `${name}.judgements.${index}.label`;
                  return (
                    <div key={field.id} className={styles.JudgementField}>
                      <FormControl
                        error={getFieldState(labelPath).error?.message}
                      >
                        <Input
                          {...register(labelPath, {
                            required: {
                              value: true,
                              message: LabelError.REQUIRED,
                            },
                            validate: (value) => {
                              const currentJudgements =
                                (getValues(`${name}.judgements`) as {
                                  label?: string;
                                }[]) || [];
                              return validate(
                                value,
                                currentJudgements.map((judgement) => ({
                                  label: judgement?.label || '',
                                })),
                                [],
                                NO_EQUIVALENT_VALUE,
                                MAX_JUDGEMENT_LABEL_LENGTH,
                                LabelErrorMessages
                              );
                            },
                          })}
                          aria-label="Judgement label"
                          placeholder="Judgement label"
                          data-testid={`${name}.judgements.${index}.label`}
                        />
                      </FormControl>
                      <input
                        type="hidden"
                        {...register(`${name}.judgements.${index}.position`, {
                          valueAsNumber: true,
                        })}
                      />
                      {judgements.fields.length > 1 && (
                        <Button
                          iconOnly
                          variant="ghost"
                          onClick={() => judgements.remove(index)}
                          data-testid={`${name}.judgements.${index}.delete`}
                        >
                          <Icon icon={IconList.light.faTrashCan}>Delete</Icon>
                        </Button>
                      )}
                    </div>
                  );
                })}
                <Link
                  variant="primary"
                  onClick={() =>
                    judgements.append({
                      label: '',
                      position: judgements.fields.length + 1,
                    })
                  }
                >
                  <Icon icon={IconList.light.faPlus} /> Add judgement
                </Link>
              </div>
            </FormControl>
            <FormControl>
              <Checkbox
                {...register(`${name}.outcome_specific`)}
                disabled={hasExtractedData}
                size={'sm'}
              >
                Can apply to individual outcome groups?
              </Checkbox>
              {showMessage(hasExtractedData)}
            </FormControl>
          </FormContainer>
        </AccordionContent>
      </AccordionGroup>
      <Tooltip hasArrow>
        <TooltipTrigger>
          <Button
            variant="ghost"
            iconOnly
            className={styles.SectionButton}
            onClick={onDuplicate}
            disabled={disableDuplicate}
            data-testid={`${name}.duplicate`}
            aria-label="Duplicate domain judgements"
            htmlType="button"
          >
            <Icon icon={IconList.light.faClone}>Duplicate</Icon>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Duplicate domain judgements</TooltipContent>
      </Tooltip>
      <Button
        variant="ghost"
        iconOnly
        className={styles.SectionButton}
        onClick={() => {
          onDelete();
          lockAccordionIfFormInvalid();
        }}
        data-testid={`${name}.delete`}
      >
        <Icon icon={IconList.light.faTrashCan}>Delete</Icon>
      </Button>
    </div>
  );
};

export default DomainForm;
